/**
 * One editor per value type.
 *
 * A setting is not a boolean, so the Value column is not a toggle. The
 * setting's own `valueType` decides the control, which is why the server
 * sends it along with the choices and the unit rather than the screen
 * keeping a second copy of the catalogue.
 */

import type { UserSettingListItem } from "@wallyo/contracts/console/configuration";
import type { SettingValue } from "@wallyo/contracts/settings";

import { settingName, t } from "../../i18n";

export function SettingValueEditor(props: {
  setting: UserSettingListItem;
  value: SettingValue;
  disabled: boolean;
  onChange: (value: SettingValue) => void;
}) {
  const { setting, value, disabled, onChange } = props;
  const { valueType, isList, choices, unit } = setting;

  // Several of them: the days a pro works, the trades they take.
  if (isList && choices) {
    const picked = Array.isArray(value) ? value.map(String) : [];
    return (
      <span className="value-list">
        {choices.map((choice) => (
          <label key={choice} className="subject-choice">
            <input
              type="checkbox"
              checked={picked.includes(choice)}
              disabled={disabled}
              onChange={(event) =>
                onChange(event.target.checked ? [...picked, choice] : picked.filter((each) => each !== choice))
              }
            />
            <span>{choice}</span>
          </label>
        ))}
      </span>
    );
  }

  if (choices) {
    return (
      <select value={String(value)} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        {choices.map((choice) => (
          <option key={choice} value={choice}>
            {choice}
          </option>
        ))}
      </select>
    );
  }

  if (typeof value === "boolean" || valueType === "Boolean") {
    const on = value === true;
    return (
      <>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={settingName(setting.settingId)}
          className={`toggle${on ? " on" : ""}`}
          disabled={disabled}
          onClick={() => onChange(!on)}
        >
          <span className="knob" />
        </button>
        <span className="value-word">{t(on ? "features.value.on" : "features.value.off")}</span>
      </>
    );
  }

  if (typeof value === "number") {
    return (
      <>
        <input
          type="number"
          className="value-number"
          value={String(value)}
          disabled={disabled}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        {unit ? <span className="value-unit">{unit}</span> : null}
      </>
    );
  }

  return (
    <input
      type="text"
      className="value-text"
      value={String(value)}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
